import LanguageDetector from 'i18next-browser-languagedetector'
import i18n from 'i18next'
import { initReactI18next, setI18n } from 'react-i18next'
import Backend from 'i18next-http-backend'

import store from '@/store'

import { setLanguage } from '@/store/languageSlice'
import { getLocalLang, getUrlParam, changedBrand } from './utils'
import { ResolvePromise } from './utils/resolvePromise'

const localLang = getLocalLang()
/** 优先使用的多语言域名 */
const I18N_URL = window.ONE_CDN_SERVER === 's3' ? process.env.S3_I18N_URL : process.env.OSS_I18N_URL
/** 备用域名 */
const backup = window.ONE_CDN_SERVER === 's3' ? process.env.OSS_I18N_URL : process.env.S3_I18N_URL
const I18N_PATH = process.env.I18N_PATH
const COMMON_LANG_PATH = process.env.COMMON_LANG_PATH

let initState: 'pending' | 'inited' = 'pending'

const assignFetch = async (params: { url: string; nameSpace: string }[], mode?: 'backup') => {
  // 是否使用兜底域名
  const domain = mode === 'backup' ? backup : I18N_URL
  try {
    const datas = await Promise.all(params.map(({ url }) => fetch(`${domain}${url}`).then((res) => res.json())))
    return datas
  } catch (error) {
    // 如果已经是兜底还报错，则抛错
    if (mode === 'backup') throw error
    // 如果不是兜底模式，则使用兜底模式再请求一遍
    return assignFetch(params, 'backup')
  }
}

/** 加载完初始化语言 */
export const i18nInitResourcePromise = new ResolvePromise<void>()

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: function (lngs: string[]) {
        const lng = lngs[0]
        if (lng === 'zh' || lng === 'zh-CN') {
          return `${I18N_PATH}/zh-hans-cn.json?lng=${lng}`
        }
        if (lng === 'zh-TW') {
          return `${I18N_PATH}/zh-hant-tw.json?lng=${lng}`
        }
        if (lng === 'zh-HK') {
          return `${I18N_PATH}/zh-hant-hk.json?lng=${lng}`
        }
        return `${I18N_PATH}/{{lng}}.json?lng=${lng}`
      },
      request: async (options, url, payload, callback) => {
        const commonUrl = url.replace(I18N_PATH, COMMON_LANG_PATH)
        const urls = [
          { url, nameSpace: 'translation' },
          { url: commonUrl, nameSpace: 'common' },
        ]
        if (initState === 'inited') {
          // 如果i18n已初始化，则直接请求
          const datas = await assignFetch(urls)
          urls.forEach((config, index) => {
            i18n.addResources(getUrlParam('lng', url), config.nameSpace, changedBrand(datas[index]))
          })
        } else {
          const lng = getUrlParam('lng', url)
          // 如果i18n未初始化，则先返回空数据，请求到之后再加上
          assignFetch(urls)
            .then((datas) => {
              if (lng === store.getState().language.lang) {
                initState = 'inited'
                i18nInitResourcePromise.resolve()
              }
              urls.forEach((config, index) => {
                i18n.addResourceBundle(lng, config.nameSpace, changedBrand(datas[index]))
              })
            })
            .catch((err) => i18nInitResourcePromise.reject(err))
        }
        callback(null, { status: 200 } as any)
      },
    },
    fallbackLng: window.localStorage.i18nextLng || 'en',
    interpolation: {
      escapeValue: false,
      skipOnVariables: false,
    },
    lng: localLang?.[0]?.lang,
    debug: false,
    supportedLngs: ['en', 'zh', 'zh-TW', 'zh-HK', 'zh-CN', 'ja'],
    keySeparator: '',
    react: {
      bindI18nStore: 'added',
    },
    load: 'currentOnly',
    initImmediate: false,
  })
  .then(() => {
    // 保证react-i18next内i18n实例一致
    setI18n(i18n)

    store.dispatch(
      setLanguage(
        localLang[0] || {
          label: 'English',
          lang: 'en',
          languageType: 'en',
        }
      )
    )
  })

export const noLang = {
  label: '显示多语言key',
  lang: 'nokey',
  languageType: 'nokey' as 'en',
  antd: 'nokey_NO',
}
export const canShowI18nKey = process.env.APP_ENV !== 'product'
if (canShowI18nKey) {
  const t = i18n.t
  i18n.t = (...args) => (i18n.language === noLang.lang ? args[0] : t.call(i18n, ...args))
}

export const LanguageChangedEvents: Set<(language: string) => void> = new Set()

export const onLanguageChanged = (fn: (language: string) => void) => {
  LanguageChangedEvents.add(fn)
  return () => {
    LanguageChangedEvents.delete(fn)
  }
}

export default i18n
